import React, { useState } from 'react';
import { formatTime } from './momentTime';
import { wordsOf } from '../../utils/wordForWord';
import { referenceCandidates, makeReference, referenceWordsIn, hasReference } from '../../utils/reference';
import './SubtitlePanel.css';

/**
 * ReferencePicker — what a word refers back to (Requirement 62): the
 * teacher taps the words it stands for, in its own subtitle or one of the
 * few before it. "j'en veux": en → "les pommes" from "Tu veux les pommes ?".
 *
 * Props:
 *   annotation — the word ({ surfaceText, startTime, subtitleId, refersTo })
 *   subtitles — the video's subtitles
 *   onSave(refersTo | null) — resolves when saved; null removes the link
 *   onCancel()
 */
function ReferencePicker({ annotation, subtitles, onSave, onCancel }) {
  const candidates = referenceCandidates(annotation, subtitles);
  const current = hasReference(annotation) ? annotation.refersTo : null;
  const startSub = (current && candidates.find((s) => s.id === current.subtitleId)) || null;
  // The words picked so far: one subtitle at a time.
  const [picked, setPicked] = useState(() => ({
    subtitleId: startSub ? startSub.id : null,
    indices: startSub ? referenceWordsIn(current, startSub) : [],
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (subtitle, i) => setPicked((prev) => {
    if (prev.subtitleId !== subtitle.id) return { subtitleId: subtitle.id, indices: [i] };
    const has = prev.indices.includes(i);
    return { subtitleId: subtitle.id, indices: has ? prev.indices.filter((x) => x !== i) : [...prev.indices, i] };
  });

  const chosenSub = candidates.find((s) => s.id === picked.subtitleId) || null;
  const ref = chosenSub ? makeReference(chosenSub, picked.indices) : null;

  const save = async (value) => {
    setSaving(true);
    setError('');
    try {
      await onSave(value);
    } catch (e) {
      console.error('Error saving the reference:', e);
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="ref-picker" role="group" aria-label={`What ${annotation.surfaceText} refers to`}>
      <p className="ref-picker-help">
        Tap the words <strong>{annotation.surfaceText}</strong> stands for here
        {' '}(<em>en</em> → “les pommes”), in its subtitle or one just before.
      </p>
      {candidates.length === 0 && (
        <p className="ref-picker-help">This word has no subtitle with French text to point back to.</p>
      )}
      {candidates.map((s) => (
        <div key={s.id} className="ref-picker-sub">
          <span className="subtitle-list-time">{formatTime(s.startTime)}</span>
          <span className="ref-picker-words">
            {wordsOf(s.originalText).map((w, i) => {
              const on = picked.subtitleId === s.id && picked.indices.includes(i);
              return (
                <button
                  key={i}
                  type="button"
                  className={`ref-picker-word${on ? ' ref-picker-word--on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggle(s, i)}
                >
                  {w}
                </button>
              );
            })}
          </span>
        </div>
      ))}
      {ref && (
        <p className="ref-picker-preview" role="status">
          {annotation.surfaceText} → “{ref.text}”
        </p>
      )}
      {error && <p className="subtitle-form-error">{error}</p>}
      <div className="subtitle-form-actions">
        <button type="button" className="subtitle-form-save-btn" disabled={!ref || saving} onClick={() => save(ref)}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="subtitle-form-cancel-btn" onClick={onCancel}>Cancel</button>
        {current && (
          <button type="button" className="subtitle-form-cancel-btn" disabled={saving} onClick={() => save(null)}>
            Remove link
          </button>
        )}
      </div>
    </div>
  );
}

export default ReferencePicker;
